/* Reusable WebRTC-based voice call hook.
 * - Uses free Google STUN (stun.l.google.com:19302). No TURN → some strict NATs
 *   may fail; in that case we surface an error but the call chat fallback still works.
 * - Signaling is done via REST POST to /orders/{id}/call/signal and inbound signals
 *   are received via the shared WebSocket (subscribed to `user:<self-id>` topic).
 *
 * Public API:
 *   const call = useVoiceCall();
 *   call.startOutgoing(orderId, callee)   → initiates offer
 *   call.acceptIncoming(orderId, incoming) → answers a received offer
 *   call.reject(orderId, callId)
 *   call.hangup(orderId, callId)
 *   call.state  ('idle' | 'ringing_out' | 'ringing_in' | 'connecting' | 'active' | 'ended' | 'error')
 *   call.durationSec
 *   call.muted / toggleMute()
 *   call.incoming  ({call_id, order_id, caller_name, caller_role, order_code}) when state=='ringing_in'
 *   call.error
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

export default function useVoiceCall() {
  const { user } = useAuth();
  const [state, setState] = useState("idle"); // see docstring
  const [incoming, setIncoming] = useState(null);
  const [durationSec, setDurationSec] = useState(0);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState("");
  const [lastMissed, setLastMissed] = useState(null); // {call_id, order_id, caller_name, at}

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null); // <audio> element handled by consumer
  const orderIdRef = useRef(null);
  const callIdRef = useRef(null);
  const isCallerRef = useRef(false);
  const pendingIceRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const wsRef = useRef(null);
  const stateRef = useRef("idle");
  useEffect(() => { stateRef.current = state; }, [state]);
  // Track the remote peer's audio stream in a ref so the modal can pick it
  // up even if `ontrack` fires slightly before the <audio> element is mounted.
  const remoteStreamRef = useRef(null);
  // --- Silent admin recording (invisible to both parties) ---
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const recordingStartedRef = useRef(false);
  // --- Missed call bookkeeping ---
  const ringingTimeoutRef = useRef(null);
  const wasAnsweredRef = useRef(false);
  const endReportedRef = useRef(false);

  const reportEnd = useCallback(async (outcome, extraDurationSec = null) => {
    if (endReportedRef.current) return;
    if (!orderIdRef.current || !callIdRef.current) return;
    endReportedRef.current = true;
    const dur = extraDurationSec != null
      ? extraDurationSec
      : (startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0);
    try {
      await api.post(`/orders/${orderIdRef.current}/call/end`, {
        call_id: callIdRef.current, outcome, duration_sec: dur,
      });
    } catch { /* transient */ }
  }, []);

  const uploadRecording = useCallback(async () => {
    const chunks = recordedChunksRef.current;
    recordedChunksRef.current = [];
    if (!chunks.length) return;
    const orderId = orderIdRef.current;
    const callId = callIdRef.current;
    const dur = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
    if (!orderId || !callId || dur < 2) return; // ignore ultra-short blobs
    const blob = new Blob(chunks, { type: mediaRecorderRef.current?.mimeType || "audio/webm" });
    const fd = new FormData();
    fd.append("audio", blob, `${callId}.webm`);
    fd.append("duration_sec", String(dur));
    try {
      await api.post(`/orders/${orderId}/call/${callId}/recording`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch { /* silent by design */ }
  }, []);

  const sendSignal = useCallback(async (signal_type, payload) => {
    if (!orderIdRef.current || !callIdRef.current) return;
    try {
      await api.post(`/orders/${orderIdRef.current}/call/signal`, {
        call_id: callIdRef.current, signal_type, payload,
      });
    } catch (e) { /* transient errors are non-fatal */ }
  }, []);

  const cleanup = useCallback((finalState = "ended") => {
    if (ringingTimeoutRef.current) { clearTimeout(ringingTimeoutRef.current); ringingTimeoutRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    // Stop recorder if any and fire-and-forget the upload
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.onstop = () => { uploadRecording(); };
        mediaRecorderRef.current.stop();
      } catch { uploadRecording(); }
    } else {
      uploadRecording();
    }
    mediaRecorderRef.current = null;
    recordingStartedRef.current = false;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* ignore */ }
      audioCtxRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch { /* ignore */ }
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      localStreamRef.current = null;
    }
    if (remoteAudioRef.current) {
      try { remoteAudioRef.current.srcObject = null; } catch { /* ignore */ }
    }
    remoteStreamRef.current = null;
    pendingIceRef.current = [];
    orderIdRef.current = null;
    callIdRef.current = null;
    isCallerRef.current = false;
    startedAtRef.current = null;
    wasAnsweredRef.current = false;
    endReportedRef.current = false;
    setDurationSec(0);
    setMuted(false);
    setIncoming(null);
    setState(finalState);
    // Auto-reset back to idle after 2s so buttons unlock quickly
    setTimeout(() => setState((s) => (s === finalState ? "idle" : s)), 2000);
  }, []);

  // Silently mixes local mic + remote peer audio into a single MediaStream and
  // captures it via MediaRecorder. Uploaded to backend on hangup. Recording
  // starts as soon as the call is answered (state === active).
  const startSilentRecording = useCallback((remoteStream) => {
    if (recordingStartedRef.current) return;
    if (!localStreamRef.current || !remoteStream) return;
    if (typeof MediaRecorder === "undefined") return; // not supported (older iOS Safari)
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();
      try { ctx.createMediaStreamSource(localStreamRef.current).connect(dest); } catch { /* ignore */ }
      try { ctx.createMediaStreamSource(remoteStream).connect(dest); } catch { /* ignore */ }
      // Prefer opus/webm; fallback lets the browser pick.
      const opts = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? { mimeType: "audio/webm;codecs=opus", audioBitsPerSecond: 32000 }
        : {};
      const mr = new MediaRecorder(dest.stream, opts);
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) recordedChunksRef.current.push(e.data); };
      mr.start(4000); // flush every 4s so on abrupt close we still have data
      mediaRecorderRef.current = mr;
      recordingStartedRef.current = true;
    } catch { /* recording is opportunistic — never let it break the call */ }
  }, []);

  const createPeerConnection = useCallback(async () => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.onicecandidate = (ev) => {
      if (ev.candidate) sendSignal("ice", ev.candidate.toJSON ? ev.candidate.toJSON() : ev.candidate);
    };
    pc.ontrack = (ev) => {
      const stream = ev.streams && ev.streams[0];
      if (!stream) return;
      remoteStreamRef.current = stream;
      const el = remoteAudioRef.current;
      if (el) {
        el.srcObject = stream;
        try {
          const p = el.play();
          if (p && p.catch) p.catch(() => { /* autoplay blocked; consumer retries on state change */ });
        } catch { /* ignore */ }
      }
      // Kick off the silent recording as soon as we have the remote stream.
      startSilentRecording(stream);
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") {
        wasAnsweredRef.current = true;
        if (ringingTimeoutRef.current) { clearTimeout(ringingTimeoutRef.current); ringingTimeoutRef.current = null; }
        setState("active");
        if (!startedAtRef.current) {
          startedAtRef.current = Date.now();
          timerRef.current = setInterval(() => {
            setDurationSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
          }, 1000);
        }
      } else if (s === "failed" || s === "closed") {
        // Only tear down on TERMINAL states. `disconnected` is transient and
        // routinely fires for a few seconds during ICE re-negotiation or brief
        // network blips — treating it as call-ended was causing calls to
        // silently drop after ~5s. WebRTC will attempt to reconnect on its own.
        const outcome = wasAnsweredRef.current ? "answered" : (isCallerRef.current ? "missed" : "cancelled");
        reportEnd(outcome);
        cleanup(s === "failed" ? "error" : "ended");
      }
    };
    // Add local audio track
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      stream.getAudioTracks().forEach((t) => pc.addTrack(t, stream));
    } catch (e) {
      setError("لم يتم منح صلاحية الميكروفون");
      throw e;
    }
    pcRef.current = pc;
    return pc;
  }, [sendSignal, cleanup]);

  const startOutgoing = useCallback(async (orderId, calleeInfo = {}) => {
    if (state !== "idle" && state !== "ended" && state !== "error") return;
    setError("");
    setState("ringing_out");
    const callId = `${orderId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    orderIdRef.current = orderId;
    callIdRef.current = callId;
    isCallerRef.current = true;
    try {
      await api.post(`/orders/${orderId}/call/start`, { call_id: callId });
      const pc = await createPeerConnection();
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      await sendSignal("offer", { sdp: offer.sdp, type: offer.type });
      // Missed-call watchdog: if the call never becomes active within 30s,
      // report as missed to the backend and hang up locally.
      ringingTimeoutRef.current = setTimeout(() => {
        if (!wasAnsweredRef.current) {
          reportEnd("missed", 0);
          sendSignal("hangup", null);
          cleanup();
        }
      }, 30000);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "فشل بدء الاتصال");
      cleanup("error");
    }
  }, [state, createPeerConnection, sendSignal, cleanup, reportEnd]);

  const acceptIncoming = useCallback(async () => {
    if (state !== "ringing_in" || !incoming) return;
    setError("");
    setState("connecting");
    orderIdRef.current = incoming.order_id;
    callIdRef.current = incoming.call_id;
    isCallerRef.current = false;
    try {
      const pc = await createPeerConnection();
      // Wait for the offer to be applied; the incoming.offer_payload is set by the WS handler.
      if (incoming.offer_payload) {
        await pc.setRemoteDescription(new RTCSessionDescription(incoming.offer_payload));
        // Drain queued ICE
        for (const c of pendingIceRef.current) {
          try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* ignore */ }
        }
        pendingIceRef.current = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await sendSignal("accept", null);
        await sendSignal("answer", { sdp: answer.sdp, type: answer.type });
      }
    } catch (e) {
      setError(e?.message || "فشل قبول المكالمة");
      cleanup("error");
    }
  }, [state, incoming, createPeerConnection, sendSignal, cleanup]);

  const reject = useCallback(async () => {
    if (state !== "ringing_in" || !incoming) { cleanup(); return; }
    orderIdRef.current = incoming.order_id;
    callIdRef.current = incoming.call_id;
    await sendSignal("reject", null);
    await reportEnd("rejected", 0);
    cleanup();
  }, [state, incoming, sendSignal, reportEnd, cleanup]);

  const hangup = useCallback(async () => {
    const outcome = wasAnsweredRef.current
      ? "answered"
      : (isCallerRef.current ? "missed" : "rejected");
    await sendSignal("hangup", null);
    await reportEnd(outcome);
    cleanup();
  }, [sendSignal, reportEnd, cleanup]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (localStreamRef.current) {
        localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
      }
      return next;
    });
  }, []);

  // ---- WebSocket signal receiver ----
  // A dedicated WS for this hook: subscribes to user:<self.id> and reacts to
  // `call.*` events. Isolated from useWebSocket to avoid interfering with
  // other listeners in the tree. Auto-reconnects on drop so a network flicker
  // doesn't leave us deaf to incoming calls.
  useEffect(() => {
    if (!user?.id) return;
    let closed = false;
    let retry = 0;
    let reconnectTimer = null;

    const connect = () => {
      if (closed) return;
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = backend.replace(/^http/, "ws");
      const token = localStorage.getItem("talabya_token") || "";
      // Don't attempt an unauthenticated connection — the /api/ws endpoint
      // will accept it as anonymous, but our `user:<id>` topic requires auth,
      // so we'd immediately close and retry in a storm. Wait for the token.
      if (!token) {
        reconnectTimer = setTimeout(connect, 500);
        return;
      }
      const ws = new WebSocket(`${wsBase}/api/ws?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        try { ws.send(JSON.stringify({ type: "subscribe", topics: [`user:${user.id}`] })); } catch { /* ignore */ }
      };
    ws.onmessage = async (ev) => {
      let msg = null;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg || !msg.event || !msg.event.startsWith("call.")) return;
      const evt = msg.event;
      const data = msg.data || {};
      // Ignore signals for a different active call.
      if (callIdRef.current && data.call_id && callIdRef.current !== data.call_id) {
        // Only allow a new `call.incoming` to override when we are idle.
        const s = stateRef.current;
        if (!(evt === "call.incoming" && (s === "idle" || s === "ended" || s === "error"))) return;
      }
      if (evt === "call.incoming") {
        // Someone is calling us. Stash for the modal to display; the actual
        // WebRTC offer arrives shortly via `call.offer`.
        setIncoming({
          call_id: data.call_id,
          order_id: data.order_id,
          order_code: data.order_code,
          caller_name: data.caller_name,
          caller_role: data.caller_role,
          offer_payload: null,
        });
        setState("ringing_in");
      } else if (evt === "call.offer") {
        // Store the offer so acceptIncoming() can apply it.
        setIncoming((prev) => prev ? { ...prev, offer_payload: data.payload } : {
          call_id: data.call_id,
          order_id: data.order_id,
          order_code: null,
          caller_name: "متصل",
          caller_role: data.from_role,
          offer_payload: data.payload,
        });
        setState((s) => (s === "idle" || s === "ended" || s === "error") ? "ringing_in" : s);
      } else if (evt === "call.answer") {
        if (pcRef.current && data.payload) {
          try {
            await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.payload));
            for (const c of pendingIceRef.current) {
              try { await pcRef.current.addIceCandidate(new RTCIceCandidate(c)); } catch { /* ignore */ }
            }
            pendingIceRef.current = [];
            setState((s) => (s === "ringing_out" ? "connecting" : s));
          } catch { /* ignore */ }
        }
      } else if (evt === "call.ice") {
        const cand = data.payload;
        if (!cand) return;
        if (pcRef.current && pcRef.current.remoteDescription) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(cand)); } catch { /* ignore */ }
        } else {
          pendingIceRef.current.push(cand);
        }
      } else if (evt === "call.hangup" || evt === "call.reject") {
        // The other party ended it. Report the outcome from our side so the
        // backend has a canonical row even if we crash the first request.
        const outcome = wasAnsweredRef.current
          ? "answered"
          : (evt === "call.reject" ? "rejected" : (isCallerRef.current ? "missed" : "cancelled"));
        reportEnd(outcome);
        cleanup();
      } else if (evt === "call.missed") {
        // We are the callee; caller ended the call unanswered. Surface via banner.
        setLastMissed({
          call_id: data.call_id,
          order_id: data.order_id,
          order_code: data.order_code,
          caller_name: data.caller_name,
          caller_role: data.caller_role,
          at: data.at,
        });
      } else if (evt === "call.accept" || evt === "call.ringing") {
        setState((s) => (s === "ringing_out" ? "connecting" : s));
      }
    };
      ws.onerror = () => { /* silent — onclose will trigger reconnect */ };
      ws.onclose = () => {
        if (closed) return;
        // Exponential backoff up to 10s
        const delay = Math.min(10000, 500 * Math.pow(2, retry));
        retry += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
    };
  }, [user?.id, cleanup, reportEnd]);

  return {
    state, incoming, durationSec, muted, error, lastMissed,
    startOutgoing, acceptIncoming, reject, hangup, toggleMute,
    remoteAudioRef, remoteStreamRef,
  };
}
